import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from '@stencil/core';

@Component({
  shadow: true,
  styleUrl: 'paf-result-timetable-row.scss',
  tag: 'paf-result-timetable-row',
})

export class PafResultTimetableRow implements ComponentInterface {

  /**
   * Define the heading text which will be placed visually-hidden
   * inside of the component.
   */
  @Prop() public heading!: string;

  /**
   * Define the heading level for the visually-hidden heading to
   * ensure a proper semantic structure
   */
  @Prop() public headingLevel!: string;

  /**
   * Define the summary text which will be placed visually-hidden
   * inside of the component.
   */
  @Prop() public summary!: string;

  /**
   * If type is expandable, you may define if it is expanded initially
   */
  @Prop({
    mutable: true,
    reflect: true,
  }) public expanded = false;

  /**
   * If set to true, the component will show a loading skeleton
   */
  @Prop() public loading?: boolean;

  /**
   * Event which will be emitted if the expandable button state changes.
   * The payload will return true if the button is expanded, false otherwise.
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'result-timetable-row__button-changed',
  })
  public timeTableRowButtonChanged: EventEmitter<boolean>;

  @Watch('expanded')
  public expandedWatcher(newValue: boolean): void {
    this._expanded = newValue;
  }

  @State() private _expanded = false;

  private _buttonClick = (evt: any): void => {
    evt.preventDefault();

    // toggle state
    this._expanded = !this._expanded;

    // emit expandable and clicked events
    this.timeTableRowButtonChanged.emit(this._expanded);
  };

  private _buttonKeyDown = (evt: any): void => {
    if (evt.which === 27) {
      evt.preventDefault();

      // toggle state
      this._expanded = false;

      // emit expandable and clicked events
      this.timeTableRowButtonChanged.emit(this._expanded);
    }
  };

  public componentWillLoad(): Promise<void> | void {
    this._expanded = this.expanded;
  }

  public render(): HTMLDivElement {
    const ariaExpanded = this._expanded?.toString() === 'true'
      ? 'true'
      : 'false';

    const HEADING_TAG = `h${this.headingLevel}`;

    return (
      <Host
        role='listitem'
      >
        <div class={{
          'loading': this.loading,
          'result-timetable-row': true,
        }}>
          <HEADING_TAG
            class='result-timetable-row__heading'
            data-testid='heading'
          >{this.heading}</HEADING_TAG>
          <button
            aria-expanded={ariaExpanded}
            aria-label={this.summary}
            class={
              {
                'result-timetable-row__button': true,
                'result-timetable-row__button--expanded': this._expanded,
              }
            }
            onClick={this._buttonClick}
            onKeyDown={this._buttonKeyDown}
            type='button'
            data-testid='expand-button'
          >
          </button>
          <div
            class='result-timetable-row__visual'
          >
            <div
              aria-hidden='true'
              class='result-timetable-row__visual-inner-wrapper'
              role='presentation'
            >
              <slot name='row-header'/>
              <slot name='pearl-chain-time'/>
              <slot name='row-footer'/>
            </div>
            <slot name='buy-button'></slot>
          </div>
          <slot name='level-2'/>
          <slot name='price-badge'/>
        </div>
      </Host>
    );
  }
}
